import { useRef, useState, type MouseEvent, type PointerEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Press } from '@dorixona/ui'
import { formatPhone } from '@dorixona/utils'
import { BackButton } from '../../ui/BackButton'
import { Icon } from '../../ui/Icon'
import { Failed, Loading } from '../../ui/Load'
import { currentLocation } from '../../app/location'
import { useMapsOn } from '../../app/settings'
import { useStores } from './api'
import { StoresFilterSheet } from './FilterSheet'
import { StoresMap } from './StoresMap'
import { RouteLink } from './RouteLink'
import {
  distanceText,
  nearestFirst,
  toQuery,
  hoursText,
  type Point,
  type StoreFilter,
} from './model'

// Звонок внутри карточки — своё действие: ни нажатие, ни его отклик до неё не доходят.
const stop = (event: MouseEvent | PointerEvent) => {
  event.stopPropagation()
}

export function StoresScreen() {
  const { t, i18n } = useTranslation(['stores', 'catalog', 'common'])
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<StoreFilter>('all')
  const [selected, setSelected] = useState<number | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)
  /** Где покупатель — после «Рядом со мной»; тогда ближние аптеки выше. */
  const [me, setMe] = useState<Point | null>(null)
  const [locating, setLocating] = useState(false)
  const [nearFailed, setNearFailed] = useState(false)
  const mapsOn = useMapsOn()
  const mapCol = useRef<HTMLDivElement>(null)

  const stores = useStores(toQuery(query, filter))
  const found = stores.data ?? []
  const rows = me ? nearestFirst(found, me) : found.map((store) => ({ store, meters: null }))
  const list = rows.map((row) => row.store)

  /** «Рядом со мной»: точка из GPS телефона (getLocation) или браузера. */
  const findNear = () => {
    setLocating(true)
    setNearFailed(false)
    void currentLocation().then((point) => {
      setLocating(false)
      if (!point) {
        setNearFailed(true)
        return
      }
      setMe(point)
      // Ближайшая — выбрана, и карта подводит к ней: покупатель рядом, в кадре.
      const nearest = nearestFirst(found, point).find((row) => row.meters !== null)
      if (nearest) setSelected(nearest.store.id)
    })
  }

  /** Булавку нажали на карте — показываем её карточку в списке. */
  const pick = (id: number) => {
    setSelected(id)
    document.getElementById(`store-${id}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }

  /**
   * Карточку нажали в списке — показываем аптеку на карте. На телефоне карта
   * над списком и к пятнадцатой аптеке уже уехала вверх — возвращаемся к ней;
   * на широком экране карта прилипла сбоку и в кадре, прокрутки нет.
   */
  const choose = (id: number) => {
    setSelected(id)
    mapCol.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }

  return (
    <section className="screen stores-wrap">
      <div className="mhead">
        <BackButton to="/profile" />
        <h1>{t('stores:title')}</h1>
      </div>

      <div className="st-tools">
        <Press
          squish="icon"
          className="hicon cs-filter"
          aria-label={t('catalog:filters')}
          onPress={() => {
            setFiltersOpen(true)
          }}
        >
          <Icon name="i-filter" />
          {filter === 'all' ? null : <span className="hbadge num">1</span>}
        </Press>
        <div className="search">
          <Icon name="i-search" size={19} />
          <input
            value={query}
            placeholder={t('stores:search')}
            aria-label={t('stores:searchLabel')}
            autoComplete="off"
            onChange={(event) => {
              setQuery(event.target.value)
            }}
          />
        </div>
      </div>

      {nearFailed ? (
        <p className="acc-note acc-note-err" role="status">
          {t('stores:nearFailed')}
        </p>
      ) : null}

      {filter === 'all' ? null : (
        <div className="fbar">
          <span className="fpill">
            {t(`stores:filters.${filter}`)}
            <button
              type="button"
              aria-label={t('stores:resetFilter', { name: t(`stores:filters.${filter}`) })}
              onClick={() => {
                setFilter('all')
              }}
            >
              ×
            </button>
          </span>
        </div>
      )}

      {/* Карты выключены в админке — «Рядом со мной» строкой над списком: ближние
          аптеки всё равно встают выше, расстояние считается без карты. */}
      {mapsOn === false ? (
        <div className="card addr-switch">
          <Press
            squish="row"
            className="opt addr-row"
            aria-pressed={me !== null}
            aria-busy={locating || undefined}
            disabled={locating}
            onPress={findNear}
          >
            <Icon name="i-locate" size={18} />
            <div className="t1">{t('stores:nearLabel')}</div>
          </Press>
        </div>
      ) : null}

      <div className={mapsOn === false ? 'st-layout st-nomap' : 'st-layout'}>
        {mapsOn ? (
          <div className="st-mapcol" ref={mapCol}>
            <div className="addr-map-box">
              <div className="st-map">
                <StoresMap stores={list} selected={selected} onSelect={pick} me={me} />
              </div>
              {/* «Рядом со мной» — поверх карты справа сверху, как на картах оформления. */}
              <Press
                squish="icon"
                className="addr-locate st-near"
                aria-label={t('stores:nearLabel')}
                aria-pressed={me !== null}
                aria-busy={locating || undefined}
                disabled={locating}
                onPress={findNear}
              >
                <Icon name="i-locate" size={20} />
              </Press>
            </div>
          </div>
        ) : null}

        <div>
          {stores.isPending ? <Loading what={t('common:state.loading')} count={4} /> : null}
          {stores.isError ? (
            <Failed
              error={stores.error}
              onRetry={() => {
                void stores.refetch()
              }}
            />
          ) : null}

          {stores.data ? (
            <>
              <div className="st-count">{t('stores:found', { count: list.length })}</div>

              {list.length === 0 ? (
                <div className="card empty">
                  <Icon name="i-search" size={54} />
                  <div className="t">{t('stores:none')}</div>
                  {t('stores:noneHint')}
                </div>
              ) : null}

              <div className="st-list">
                {rows.map(({ store, meters }, index) => (
                  // Нажимается вся карточка, а не одна шапка: мимо кнопок палец
                  // тоже попадает в аптеку. Кнопка внутри — для клавиатуры и
                  // чтения с экрана; её click всплывает сюда.
                  <Press
                    as="div"
                    squish="row"
                    key={store.id}
                    id={`store-${store.id}`}
                    className={store.id === selected ? 'scard on' : 'scard'}
                    onPress={() => {
                      choose(store.id)
                    }}
                  >
                    <button type="button" className="sc-h" aria-pressed={store.id === selected}>
                      <span className="sc-n num" aria-hidden="true">
                        {index + 1}
                      </span>
                      <span className="sc-b">
                        <span className="sc-t">
                          {store.name}
                          {meters !== null ? (
                            <span className="sc-dist num">
                              {distanceText(meters, t, i18n.language)}
                            </span>
                          ) : null}
                        </span>
                        <span className="sc-a">
                          {store.address}
                          {store.metro ? ` · ${t('stores:metro', { name: store.metro })}` : ''}
                        </span>
                        <span className={store.openNow ? 'sc-open ok' : 'sc-open no'}>
                          {store.is247
                            ? null
                            : `${t(store.openNow ? 'stores:open' : 'stores:closed')} · `}
                          {hoursText(store, t)}
                        </span>
                      </span>
                    </button>
                    <div className="sc-f">
                      {store.phone ? (
                        <a
                          className="sc-call num"
                          href={`tel:+${store.phone.replace(/\D/g, '')}`}
                          onClick={stop}
                          onPointerDown={stop}
                        >
                          <Icon name="i-phone" size={15} />
                          {formatPhone(store.phone)}
                        </a>
                      ) : null}
                      <RouteLink
                        store={store}
                        className="sc-call"
                        label={t('stores:routeLabel', { name: store.name })}
                      >
                        <Icon name="i-pin" size={15} />
                        {t('stores:route')}
                      </RouteLink>
                    </div>
                  </Press>
                ))}
              </div>
            </>
          ) : null}
        </div>
      </div>

      <StoresFilterSheet
        opened={filtersOpen}
        filter={filter}
        found={stores.data ? list.length : undefined}
        onChange={setFilter}
        onClose={() => {
          setFiltersOpen(false)
        }}
      />
    </section>
  )
}
